import { useRef, useEffect, useState } from 'react';
import { Button } from '../components/Button.jsx';
import { PAGE } from '../state/pages.js';
import { GAME_MODE, DEFAULT_GAME_MODE, GAME_MODE_LABEL } from '../data/modes.js';
import { AudioSettings } from '../audio/AudioSettings.jsx';
import { useAudio } from '../audio/AudioProvider.jsx';

const FLICKER_INITIAL_DELAY_MS = 4000;
export const HOME_FLICKER_REPEAT_MS = 58000;

export function HomePage({
  onNavigate,
  gameMode = DEFAULT_GAME_MODE,
  onGameModeChange
}) {
  const mainRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [flickerActive, setFlickerActive] = useState(false);
  const { unlocked, playCue } = useAudio();

  useEffect(() => {
    mainRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFlickerActive(true);
    }, FLICKER_INITIAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!unlocked) return undefined;
    const elapsed = Date.now() - mountedAtRef.current;
    const cycleElapsed = (elapsed - FLICKER_INITIAL_DELAY_MS) % HOME_FLICKER_REPEAT_MS;
    const remaining = elapsed < FLICKER_INITIAL_DELAY_MS
      ? FLICKER_INITIAL_DELAY_MS - elapsed
      : cycleElapsed === 0
        ? 0
        : HOME_FLICKER_REPEAT_MS - cycleElapsed;
    let interval;
    const timer = window.setTimeout(() => {
      playCue('lightningFlicker');
      interval = window.setInterval(() => {
        playCue('lightningFlicker');
      }, HOME_FLICKER_REPEAT_MS);
    }, remaining);
    return () => {
      window.clearTimeout(timer);
      if (interval) window.clearInterval(interval);
    };
  }, [playCue, unlocked]);

  return (
    <main
      ref={mainRef}
      tabIndex={-1}
      className={
        'onboarding-screen onboarding-home' +
        (flickerActive ? ' onboarding-home-flicker-active' : '')
      }
    >
      <div className="onboarding-content">
        <h1 className="onboarding-title onboarding-title-brand">
          Shortfall
          <span className="onboarding-title-bolt" aria-hidden="true">
            {'\u26a1'}
          </span>
        </h1>
        <p className="onboarding-subtitle">
          A strategy game about energy choices and carbon costs
        </p>

        <div className="onboarding-actions onboarding-home-actions">
          <div className="onboarding-home-actions-primary">
            <Button
              onClick={() => onNavigate(PAGE.SETUP_NAME)}
              changesScreen
              testId="home-new-game"
            >
              New Game
            </Button>
          </div>
          {onGameModeChange ? (
            <div className="home-mode-select">
              <p className="home-mode-label" id="home-mode-label">
                Select game mode
              </p>
              <div
                className="home-mode-options"
                role="radiogroup"
                aria-labelledby="home-mode-label"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={gameMode === GAME_MODE.CLASSIC}
                  className={
                    'home-mode-option' +
                    (gameMode === GAME_MODE.CLASSIC
                      ? ' home-mode-option-active'
                      : '')
                  }
                  onClick={() => onGameModeChange(GAME_MODE.CLASSIC)}
                  data-testid="home-mode-classic"
                  data-audio-cue="buttonSecondary"
                >
                  Classic
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={gameMode === GAME_MODE.ADVANCED_CAPACITY}
                  className={
                    'home-mode-option' +
                    (gameMode === GAME_MODE.ADVANCED_CAPACITY
                      ? ' home-mode-option-active'
                      : '')
                  }
                  onClick={() => onGameModeChange(GAME_MODE.ADVANCED_CAPACITY)}
                  data-testid="home-mode-advanced"
                  data-audio-cue="buttonSecondary"
                >
                  Advanced
                </button>
              </div>
            </div>
          ) : null}
          {/* The score buttons name the selected mode, so it is clear from
              here which of the two boards they open. */}
          <div className="onboarding-home-actions-secondary">
            <Button
              variant="secondary"
              onClick={() => onNavigate(PAGE.DAILY_HIGH_SCORES)}
              changesScreen
              testId="home-daily-high-scores"
            >
              {`${GAME_MODE_LABEL[gameMode]} Daily High Scores`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => onNavigate(PAGE.ALL_TIME_HIGH_SCORES)}
              changesScreen
              testId="home-all-time-high-scores"
            >
              {`${GAME_MODE_LABEL[gameMode]} All-Time High Scores`}
            </Button>
          </div>
          <div className="onboarding-home-actions-primary">
            <Button
              onClick={() => onNavigate(PAGE.OPTIMISATION_LAB)}
              changesScreen
              testId="home-optimisation-lab"
            >
              Optimisation Lab
            </Button>
          </div>
        </div>

        <details
          className="onboarding-advanced onboarding-audio-settings"
          onToggle={(event) => setAudioOpen(event.currentTarget.open)}
        >
          <summary data-audio-cue="buttonSecondary">Audio</summary>
          {audioOpen ? (
            <div className="onboarding-advanced-content">
              <AudioSettings variant="inline" />
            </div>
          ) : null}
        </details>

        <section
          className={
            'onboarding-data-note' +
            (disclaimerOpen ? ' onboarding-data-note-open' : '')
          }
        >
          <button
            type="button"
            className="onboarding-data-note-toggle"
            onClick={() => setDisclaimerOpen((open) => !open)}
            aria-expanded={disclaimerOpen}
            data-testid="home-disclaimer"
            data-audio-cue="buttonSecondary"
          >
            Data note
          </button>
          {disclaimerOpen ? (
            <p className="onboarding-disclaimer-text" role="status">
              We store your planner ID, country, role, score, and completed
              power-plant portfolio to show high scores and improve the teaching
              version of the game. Do not use your real name if you do not want
              it shown.
            </p>
          ) : null}
        </section>

        <section
          className={
            'onboarding-data-note' +
            (creditsOpen ? ' onboarding-data-note-open' : '')
          }
        >
          <button
            type="button"
            className="onboarding-data-note-toggle"
            onClick={() => setCreditsOpen((open) => !open)}
            aria-expanded={creditsOpen}
            data-testid="home-credits"
            data-audio-cue="buttonSecondary"
          >
            Credits
          </button>
          {creditsOpen ? (
            <p className="onboarding-disclaimer-text" role="status">
              Shortfall was created by Leigh Martindale as an educational
              power-grid optimisation game. Music: Substation Drift (Suno).
              Voice cues: Eleven Labs. Built with React, Vite, Netlify
              Functions, and Netlify Blobs. Optimisation Lab uses HiGHS 1.15.1,
              developed by the University of Edinburgh, through highs-js
              1.15.3. Copyright 2023 highs-js and 2026 HiGHS. Both are provided
              under the MIT Licence.{' '}
              <a href="/highs-mit-licence.txt" target="_blank" rel="noreferrer">
                Read the licence
              </a>
              . No AI was harmed in the making of this game.
            </p>
          ) : null}
        </section>
      </div>
    </main>
  );
}
